import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";
import type { StepExecution, StepExecutionStatus } from "../../api/client.ts";
import { formatNumber } from "../../utils/format.ts";

export const EXECUTION_COLORS: Record<
  StepExecutionStatus,
  "default" | "success" | "error" | "warning"
> = {
  pending: "warning",
  success: "success",
  failed: "error",
  skipped: "default",
  awaiting_approval: "warning",
  rejected: "default",
};

export const EXECUTION_STATUS_LABELS: Record<StepExecutionStatus, string> = {
  pending: "running",
  success: "done",
  failed: "failed",
  skipped: "skipped",
  awaiting_approval: "waiting for approval",
  rejected: "rejected",
};

/** A folded list of steps in one line: "3 · 1 waiting for approval, 2 done". */
export function executionGist(executions: StepExecution[]): string {
  const counts = new Map<string, number>();
  for (const e of executions) {
    const label = EXECUTION_STATUS_LABELS[e.status] ?? e.status;
    counts.set(label, (counts.get(label) ?? 0) + 1);
  }
  const parts = [...counts].map(([label, n]) => `${n} ${label}`);
  return `${formatNumber(executions.length)} · ${parts.join(", ")}`;
}

/** What a step's outcome says beyond its status, for a chip or a line. */
export function executionDetail(e: StepExecution): string | null {
  if (e.error) return e.error;
  const r = e.result;
  if (e.step_type === "agent" && typeof r.result_count === "number") {
    return `${r.result_count} prospect${r.result_count === 1 ? "" : "s"}`;
  }
  if (e.step_type === "agent" && typeof r.verdict === "string") return String(r.verdict);
  if (e.step_type === "add_to_prospect_list" && typeof r.added === "number") {
    return `${r.added} copied`;
  }
  if (e.step_type === "slack_message" && e.status === "pending") return "queued";
  if (e.status === "skipped" && typeof r.reason === "string") return r.reason;
  if (e.status === "rejected" && e.review_note) return e.review_note;
  return null;
}

/** One workflow step's execution as a chip: its label, status and a short outcome. */
export default function ExecutionStatusChip({
  execution,
  label,
}: {
  execution: StepExecution;
  label?: string;
}) {
  const detail = executionDetail(execution);
  const text = `${label ?? execution.step_label}: ${EXECUTION_STATUS_LABELS[execution.status] ?? execution.status}${detail ? ` · ${detail}` : ""}`;
  return (
    <Tooltip title={execution.error ?? ""} describeChild>
      <Chip
        size="small"
        label={text}
        color={EXECUTION_COLORS[execution.status] ?? "default"}
        sx={{ maxWidth: 420 }}
      />
    </Tooltip>
  );
}
